import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { AuthLoading } from "@/components/auth-loading";
import { EmailVerificationModal } from "@/components/email-verification-modal";
import { MemoriesIntroModal, useMemoriesIntroStore } from "@/components/memories-intro-modal";
import { OnboardingDialog } from "@/components/onboarding/onboarding-dialog";
import { ScheduleModal } from "@/components/schedule-modal";
import { SiteHeader } from "@/components/site-header";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { UpgradeModal } from "@/components/upgrade-modal";
import { useRecentRuns } from "@/lib/api/hooks/tasks";
import { ErrorBoundary } from "@/lib/errors/boundary";
import { buildMetaTags } from "@/lib/seo/meta";
import { useAuthStore } from "@/stores/auth-store";
import { useOnboardingStore } from "@/stores/onboarding-store";

export const Route = createFileRoute("/_dashboard")({
	// Protect all dashboard routes - redirect to login if not authenticated
	beforeLoad: ({ location }) => {
		if (typeof window === "undefined") {
			return;
		}

		const { isAuthenticated } = useAuthStore.getState();

		if (!isAuthenticated) {
			throw redirect({
				to: "/login",
				search: {
					// Preserve the redirect URL so we can navigate back after login
					redirect: location.pathname,
				},
			});
		}

		return;
	},

	head: () => {
		// Layout-level SEO defaults for all dashboard pages
		// Individual child routes can override these
		const meta = buildMetaTags({
			// Dashboard pages require authentication, so don't index
			noIndex: true,
			noFollow: true,
		});

		return { meta };
	},

	component: DashboardLayout,
});

function DashboardLayout() {
	const navigate = useNavigate();
	const { completed, skipOnboarding, openOnboarding } = useOnboardingStore();
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const isInitialized = useAuthStore((state) => state.isInitialized);
	const user = useAuthStore((s) => s.user);
	const openMemoriesIntro = useMemoriesIntroStore((s) => s.open);
	const [hasMounted, setHasMounted] = useState(false);

	// Fetch recent runs — 5 needed for the memories intro trigger
	const { data: runs, isLoading: runsLoading } = useRecentRuns(5);
	const hasRuns = (runs?.length ?? 0) > 0;

	useEffect(() => {
		setHasMounted(true);
	}, []);

	useEffect(() => {
		if (!hasMounted || !isInitialized) {
			return;
		}

		if (!isAuthenticated) {
			const redirectTo = typeof window !== "undefined" ? window.location.pathname : "/";

			navigate({
				to: "/login",
				search: {
					redirect: redirectTo,
				},
				replace: true,
			});
		}
	}, [hasMounted, isInitialized, isAuthenticated, navigate]);

	// Mark onboarding as completed for existing users (so localStorage stays in sync)
	useEffect(() => {
		if (hasRuns && !completed) skipOnboarding();
	}, [hasRuns, completed, skipOnboarding]);

	// Show onboarding on first login (if not completed and no existing runs)
	useEffect(() => {
		if (!hasMounted || !isInitialized || completed || !isAuthenticated || runsLoading || hasRuns) {
			return;
		}

		const timer = setTimeout(() => openOnboarding(), 500);
		return () => clearTimeout(timer);
	}, [completed, hasMounted, isAuthenticated, isInitialized, openOnboarding, runsLoading, hasRuns]);

	// Show memories intro modal after 5 runs (if not already dismissed)
	useEffect(() => {
		if (!hasMounted || !isInitialized || !isAuthenticated) return;
		if (user?.memories_intro_dismissed) return;
		if (runsLoading || (runs?.length ?? 0) < 5) return;
		const timer = setTimeout(() => openMemoriesIntro(), 500);
		return () => clearTimeout(timer);
	}, [
		hasMounted,
		isInitialized,
		isAuthenticated,
		user?.memories_intro_dismissed,
		runs,
		runsLoading,
		openMemoriesIntro,
	]);

	// Show loading to prevent exposing protected content before auth verification
	if (!hasMounted || !isInitialized || !isAuthenticated) {
		return <AuthLoading />;
	}

	return (
		<SidebarProvider
			style={
				{
					"--sidebar-width": "calc(var(--spacing) * 56)",
					"--header-height": "calc(var(--spacing) * 12)",
				} as React.CSSProperties
			}
		>
			<AppSidebar variant="inset" />
			<SidebarInset>
				<SiteHeader />
				<div className="flex flex-1 flex-col">
					<div className="@container/main flex flex-1 flex-col gap-2">
						<div className="flex flex-col gap-4 pt-4 md:gap-6 md:pt-6">
							<Outlet />
						</div>
					</div>
				</div>
			</SidebarInset>

			{/* Global modals */}
			<ErrorBoundary fallback={null}>
				<OnboardingDialog />
			</ErrorBoundary>
			<EmailVerificationModal />
			<UpgradeModal />
			<ScheduleModal />
			<MemoriesIntroModal />
		</SidebarProvider>
	);
}
